import { Column } from 'react-table';

export type ExcelExportPolicy = 'INCLUDE' | 'EXCLUDE';

export type ExcelExportPolicyEvaluationType = 'IMPLICIT' | 'EXPLICIT';

export interface ExcelExportPolicyEvaluation {
  type: ExcelExportPolicyEvaluationType;
  result: ExcelExportPolicy;
}

export interface ExcelExportOptions<T extends Record<string, unknown>> {
  /**
   * @default `INCLUDE`
   */
  exportPolicy?: ExcelExportPolicy;
  columnName?: string;
  getValue?: (
    item: T,
    context: {
      index: number;
      selectedRowIds: Readonly<Record<string, boolean>>;
      allRows: readonly T[];
    },
  ) => string | number;
}

export interface ExcelTableColumn<T extends Record<string, unknown>> {
  excel: ExcelExportOptions<T>;
}

export function isExcelTableColumn<T extends Record<string, unknown>>(
  column: Column<T>,
): column is Column<T> & ExcelTableColumn<T> {
  return !!column['excel'];
}

export function isExplicitInclude(input: ExcelExportPolicyEvaluation): boolean {
  return input.result === 'INCLUDE' && input.type === 'EXPLICIT';
}

export function isAnyInclude(input: ExcelExportPolicyEvaluation): boolean {
  return input.result === 'INCLUDE';
}

export function evaluateColumnExportPolicy<T extends Record<string, unknown>>(
  column: Column<T>,
): ExcelExportPolicyEvaluation {
  if (isExcelTableColumn(column)) {
    if (column.excel.exportPolicy) {
      return {
        type: 'EXPLICIT',
        result: column.excel.exportPolicy,
      };
    }
  }

  return {
    type: 'IMPLICIT',
    result: 'INCLUDE',
  };
}

export function withExcelOptions<T extends Record<string, unknown>>(options: ExcelExportOptions<T>): Column<T> {
  return (({
    excel: options,
  } as ExcelTableColumn<T>) as unknown) as Column<T>;
}
